import type { ReactNode } from "react"
import { observer } from "mobx-react-lite"

import type { TokenStore } from "@/domains/tokens"
import { Button } from "@/components/button"
import { BodyXs } from "@/domains/design/components/body-xs"
import { cn } from "@/domains/library"
import { AprBadge } from "@/domains/thick-v1/components/apr-badge"
import { IndexerPendingIndicator } from "@/domains/thick-v1/components/indexer-pending-indicator"
import { DisplayTokens } from "@/domains/tokens/components/display-tokens"

/**
 * Prop contract for the manage-position header.
 *
 * The pair carries live `TokenStore` instances so the header can render
 * logos via `DisplayTokens` and so the MobX token migration keeps moving
 * forward. Everything else is still pre-formatted by the composite, which
 * keeps the rendering logic simple and Storybook stories cheap (mock
 * `TokenStore` fixtures already live under `@/domains/tokens/components/
 * token-dialog/0.story-mocks`). Because the tokens are observable, the
 * component below is wrapped in `observer`.
 */
export type ManagePositionHeaderViewProps = {
  pair: {
    token0: TokenStore
    token1: TokenStore
    displayName: string
    feeLabel: string
  }
  tokenId: string
  /**
   * True while the indexer has not yet surfaced the on-chain NFP `tokenId`
   * for this position (pending indexer Proposal 4). Drives a yellow-triangle
   * pending indicator next to the NFP ID and can be used by parent views to
   * disable write actions.
   */
  nfpmTokenIdPending?: boolean
  displayRange: string
  displayMidPrice: string
  isInRange: boolean
  /**
   * Lifetime-realised APR for the position — pre-formatted by the
   * `ManualClPositionStore.realisedApr` computed. `"—"` renders neutral when
   * the position has no accrued fees or otherwise cannot be measured.
   */
  realisedAprFormatted: string
  /**
   * Forward-looking "current rate" APR alongside its daily-USD projection.
   * `inRange` drives the out-of-range treatment on the secondary badge.
   */
  currentRate: {
    formattedPct: string
    formattedDailyUsd: string
    inRange: boolean
    label?: string
    status?: "ready" | "out-of-range" | "unindexed-pair"
  }
  /**
   * Percentage of the lookback window during which the pool's traded price
   * sat inside this position's range. `undefined` when hourly data is
   * missing — UI renders a neutral dash.
   */
  timeInRangePct7d: number | undefined
  /**
   * Number of completed hourly rows that fed `timeInRangePct7d`. Drives
   * the honest lookback label: "of last 7d" for a full 168h window,
   * "since opened" when the position is younger than seven days. Undefined
   * when we don't have any completed hours yet (e.g. position is a few
   * minutes old).
   */
  timeInRangeSampleHours?: number | undefined
  onClaimFees?: () => void
  /** Mirrors `CollectFeesStore.canSubmit` — gates the header collect action. */
  canCollectFees?: boolean
  /** Mirrors `CollectFeesStore.isSubmitting` — drives the loading label. */
  isCollectingFees?: boolean
  trailingActions?: ReactNode
}

/**
 * Uniswap v3-style header for the manage-position layout.
 *
 * Surfaces the position's pair, NFP id, current range + price, the in-range
 * pill, and a one-click collect-fees action. `trailingActions` is a slot for the
 * settings gear so
 * the composite owner can wire it up without the view importing the popover.
 */
export const ManagePositionHeaderView = observer(function ManagePositionHeaderView({
  pair,
  tokenId,
  nfpmTokenIdPending = false,
  displayRange,
  displayMidPrice,
  isInRange,
  realisedAprFormatted,
  currentRate,
  timeInRangePct7d,
  timeInRangeSampleHours,
  onClaimFees,
  canCollectFees = false,
  isCollectingFees = false,
  trailingActions,
}: ManagePositionHeaderViewProps) {
  const currentAprStatus = currentRate.status ?? "ready"
  const showCurrentApr = currentAprStatus === "ready" && hasAprValue(currentRate.formattedPct)
  const collectFeesLabel = isCollectingFees
    ? "Collecting..."
    : nfpmTokenIdPending
      ? "Resolving NFP id…"
      : "Collect fees"

  return (
    <div className="flex flex-col gap-4 rounded-lg border border-p400-light bg-p400 p-4">
      <div className="flex flex-col items-start gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3">
          <DisplayTokens tokens={[pair.token0, pair.token1]} tokenSize={30} />
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <span className="text-base font-medium text-t100">{pair.displayName}</span>
              <span className="text-xs text-t300">{pair.feeLabel}</span>
              <span
                className={cn(
                  "rounded px-2 py-0.5 text-[10px] font-medium",
                  isInRange ? "bg-primary-400/20 text-primary-400" : "bg-error-background/15 text-error-text",
                )}
              >
                {isInRange ? "In range" : "Out of range"}
              </span>
            </div>
            <span className="flex items-center gap-1 text-xs text-t200">
              NFP ID {nfpmTokenIdPending ? "—" : `#${tokenId}`}
              {nfpmTokenIdPending && (
                <IndexerPendingIndicator proposal="P4" size="xs" ariaLabel="NFP ID pending indexer Proposal 4" />
              )}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {onClaimFees ? (
            <Button
              variant="neutral"
              size="md"
              onClick={onClaimFees}
              disabled={!canCollectFees || isCollectingFees || nfpmTokenIdPending}
              title={nfpmTokenIdPending ? "On-chain NFP id is still resolving for this position" : undefined}
            >
              {collectFeesLabel}
            </Button>
          ) : null}
          {trailingActions}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <HeaderStat label="Range" value={displayRange} />
        <HeaderStat label="Current price" value={displayMidPrice || "—"} />
      </div>

      <div className="grid grid-cols-2 gap-4 rounded-md border border-p300-light bg-p300 p-3 md:grid-cols-3">
        <div className="flex flex-col gap-2">
          <BodyXs className="uppercase tracking-wide text-t300">Position APR</BodyXs>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
            {hasAprValue(realisedAprFormatted) ? (
              <HeaderAprRow label="Accrued" value={realisedAprFormatted} variant="realised" />
            ) : null}
            {showCurrentApr ? (
              <HeaderAprRow
                label={currentRate.label ?? "Current"}
                value={currentRate.formattedPct}
                variant="estimated"
              />
            ) : null}
            {currentAprStatus !== "ready" ? (
              <span className="text-xs text-t300">{currentRate.label ?? "No pricing"}</span>
            ) : null}
            {currentRate.inRange && currentRate.formattedDailyUsd ? (
              <span className="text-[11px] text-t300">≈ {currentRate.formattedDailyUsd}</span>
            ) : null}
          </div>
        </div>
        <HeaderStat
          label={resolveTimeInRangeLabel(timeInRangeSampleHours)}
          value={
            timeInRangePct7d === undefined
              ? "—"
              : `${timeInRangePct7d.toFixed(0)}% ${resolveTimeInRangeWindowCopy(timeInRangeSampleHours)}`
          }
        />
      </div>
    </div>
  )
})

const HeaderStat = ({ label, value }: { label: string; value: string }) => (
  <div className="flex flex-col">
    <BodyXs className="uppercase tracking-wide text-t300">{label}</BodyXs>
    <span className="truncate text-sm font-medium text-t100">{value}</span>
  </div>
)

/**
 * Two-line APR tile used inside the "Position APR" card: tiny uppercase
 * caption above a full-weight `AprBadge` so "Realised" and "Current" reach
 * the user with the same visual weight. Keeps the semantic variants
 * (green-solid for realised, dashed for projection) while dropping the
 * muted inline-text treatment that used to bury the current rate next to
 * the lifetime number.
 */
const HeaderAprRow = ({
  label,
  value,
  variant,
  outOfRange = false,
}: {
  label: string
  value: string
  variant: "realised" | "estimated"
  outOfRange?: boolean
}) => (
  <div className="flex flex-col items-start gap-0.5">
    <span className="text-[10px] uppercase leading-tight tracking-wider text-t300">{label}</span>
    <AprBadge value={value} variant={variant} outOfRange={outOfRange} size="sm" />
  </div>
)

/**
 * Treat both the empty string and the literal em-dash as "no data yet"
 * so the caller can hide the whole labeled row instead of showing a
 * bare dash next to a prominent "Realised" caption.
 */
const hasAprValue = (value: string): boolean => value.length > 0 && value !== "—"

const FULL_WEEK_HOURS = 24 * 7

/**
 * A position younger than seven days should not carry the "(7d)" suffix on
 * its label: the window is a subset of the week. We flip the label to plain
 * "Time in range" and move the scoping into the value string ("since
 * opened") so users can't misread a brand-new position's stat as a
 * week-long average.
 */
const resolveTimeInRangeLabel = (sampleHours: number | undefined): string => {
  if (sampleHours === undefined) return "Time in range"
  if (sampleHours >= FULL_WEEK_HOURS) return "Time in range (7d)"
  return "Time in range"
}

const resolveTimeInRangeWindowCopy = (sampleHours: number | undefined): string => {
  if (sampleHours === undefined) return ""
  if (sampleHours >= FULL_WEEK_HOURS) return "of last 7d"
  return "since opened"
}
